import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Phone, Users, Calendar, Camera, LogOut, CheckCircle, XCircle, AlertTriangle, Car, MessageSquare, Shield, MapPin, Volume2, VolumeX, Bell, BellOff } from 'lucide-react';
import { DriverWallet } from '../components/DriverWallet';
import { API as API_URL, BACKEND_URL } from '../utils/apiConfig';
import { getAudioAlertsEnabled, setAudioAlertsEnabled } from '../hooks/useMatchChime';
import { usePushNotifications } from '../hooks/usePushNotifications';
import './Profile.css';

export const Profile = () => {
  const { t } = useTranslation();
  const { user, logout, updateProfile, uploadProfilePhoto } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const vehiclePhotoInputRef = useRef(null);
  const [vehiclePhotoUrl, setVehiclePhotoUrl] = useState(null);
  const [vehiclePhotoUploading, setVehiclePhotoUploading] = useState(false);
  const { supported: pushSupported, subscribed: pushSubscribed, loading: pushLoading, error: pushError, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe, permission: pushPermission, swReady } = usePushNotifications();
  
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    role: user?.role || '',
    bio: user?.bio || '',
    city: user?.city || '',
    vehicle: {
      make: user?.vehicle?.make || '',
      model: user?.vehicle?.model || '',
      color: user?.vehicle?.color || '',
      license_plate: user?.vehicle?.license_plate || '',
      has_ac: user?.vehicle?.has_ac ?? true
    },
    preferences: {
      smoking: user?.preferences?.smoking || 'non-smoker',
      pets: user?.preferences?.pets || 'none',
      music: user?.preferences?.music || 'yes'
    }
  });

  // Sync formData whenever user object changes (e.g., after loadUser fetches vehicle)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: user.firstName || prev.firstName || '',
        lastName: user.lastName || prev.lastName || '',
        phone: user.phone || prev.phone || '',
        role: user.role || prev.role || '',
        bio: user.bio || prev.bio || '',
        city: user.city || prev.city || '',
        vehicle: {
          make: user.vehicle?.make || prev.vehicle?.make || '',
          model: user.vehicle?.model || prev.vehicle?.model || '',
          color: user.vehicle?.color || prev.vehicle?.color || '',
          license_plate: user.vehicle?.license_plate || prev.vehicle?.license_plate || '',
          has_ac: user.vehicle?.has_ac ?? prev.vehicle?.has_ac ?? true
        },
        preferences: {
          smoking: user.preferences?.smoking || prev.preferences?.smoking || 'non-smoker',
          pets: user.preferences?.pets || prev.preferences?.pets || 'none',
          music: user.preferences?.music || prev.preferences?.music || 'yes'
        }
      }));
      // Phase A1 — pull vehicle photo from /my-vehicle on mount
      if (user.vehicle?.photo_url || user.vehicle?.photoUrl) {
        setVehiclePhotoUrl(user.vehicle.photo_url || user.vehicle.photoUrl);
      }
    }
  }, [user]);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [trackingInterval, setTrackingInterval] = useState(10);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(getAudioAlertsEnabled());

  // Load tracking interval from backend
  useEffect(() => {
    const loadTrackingInterval = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API_URL}/rides/settings/tracking-interval`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setTrackingInterval(data.interval || 10);
        }
      } catch {}
    };
    loadTrackingInterval();
  }, []);

  const handleTrackingIntervalChange = async (newInterval) => {
    setTrackingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/rides/settings/tracking-interval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ interval: newInterval })
      });
      if (resp.ok) {
        setTrackingInterval(newInterval);
        setMessage({ type: 'success', text: t('security.trackingInterval.saved') });
      } else {
        setMessage({ type: 'error', text: t('security.trackingInterval.saveError') });
      }
    } catch {
      setMessage({ type: 'error', text: t('security.trackingInterval.saveError') });
    }
    setTrackingLoading(false);
    setTimeout(() => setMessage(null), 3000);
  };

  const handleAudioToggle = () => {
    const newVal = !audioEnabled;
    setAudioEnabled(newVal);
    setAudioAlertsEnabled(newVal);
    setMessage({
      type: 'success',
      text: newVal ? t('security.audioAlerts.enabled') : t('security.audioAlerts.disabled')
    });
    setTimeout(() => setMessage(null), 3000);
  };

  const handlePushToggle = async () => {
    if (pushSubscribed) {
      await pushUnsubscribe();
      setMessage({ type: 'success', text: t('security.pushNotifications.disabled') });
    } else {
      const ok = await pushSubscribe();
      if (ok) {
        setMessage({ type: 'success', text: t('security.pushNotifications.enabled') });
      } else {
        // Show specific error based on failure reason
        setMessage({ type: 'error', text: t('security.pushNotifications.denied') });
      }
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const handleTestPush = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/push/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        setMessage({ type: 'success', text: t('security.pushNotifications.testSent') });
      } else {
        setMessage({ type: 'error', text: t('security.pushNotifications.testFailed') });
      }
    } catch {
      setMessage({ type: 'error', text: t('security.pushNotifications.testFailed') });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const result = await updateProfile(formData);
    
    if (result.success) {
      setMessage({ type: 'success', text: t('profile.messages.profileUpdated') });
      setEditing(false);
    } else {
      setMessage({ type: 'error', text: result.error });
    }
    
    setTimeout(() => setMessage(null), 3000);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const result = await uploadProfilePhoto(file);
    
    if (result.success) {
      setMessage({ type: 'success', text: t('profile.messages.photoUploaded') });
    } else {
      setMessage({ type: 'error', text: result.error });
    }
    
    setUploading(false);
    setTimeout(() => setMessage(null), 3000);
  };

  // Phase A1 — Foto vom Auto
  const handleVehiclePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVehiclePhotoUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${API_URL}/users/vehicle-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await resp.json();
      if (resp.ok && data.photo_url) {
        setVehiclePhotoUrl(data.photo_url);
        setMessage({ type: 'success', text: t('profile.messages.vehiclePhotoUploaded', 'Auto-Foto aktualisiert') });
      } else {
        setMessage({ type: 'error', text: data.detail || t('profile.messages.vehiclePhotoError', 'Upload fehlgeschlagen') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('profile.messages.vehiclePhotoError', 'Upload fehlgeschlagen') });
    } finally {
      setVehiclePhotoUploading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        logout();
        navigate('/');
      } else {
        setMessage({ type: 'error', text: t('profile.messages.deleteAccountError') });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Delete account error:', error);
      setMessage({ type: 'error', text: t('profile.messages.deleteAccountError') });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-photo-section">
            <div className="profile-photo" data-testid="profile-photo">
              {user?.profilePhoto ? (
                <img src={user.profilePhoto} alt="Profile" />
              ) : (
                <User size={64} />
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="photo-upload-button"
              disabled={uploading}
              data-testid="upload-photo-button"
            >
              <Camera size={16} />
              {uploading ? t('profile.upload.uploading') : t('profile.upload.changePhoto')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
            />
          </div>
          
          <div className="profile-info">
            <h1>{user?.firstName} {user?.lastName}</h1>
            <p>{user?.email}</p>
            <div className="verification-status">
              {user?.verified ? (
                <span className="verified" data-testid="verified-status">
                  <CheckCircle size={16} /> {t('profile.verification.phoneVerified')}
                </span>
              ) : (
                <span className="unverified" data-testid="unverified-status">
                  <XCircle size={16} /> {t('profile.verification.phoneNotVerified')}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {message && (
          <div className={`message-box ${message.type}`} data-testid="message">
            {message.text}
          </div>
        )}

        {/* ---- EDIT / SAVE BUTTONS AT TOP (always visible) ---- */}
        <div data-testid="profile-action-bar" style={{
          display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', alignItems: 'center',
          padding: '0.75rem 0', borderBottom: editing ? '2px solid #2563eb' : '1px solid #e2e8f0',
          marginBottom: '1rem', position: 'sticky', top: 0, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', zIndex: 10
        }}>
          {editing && (
            <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: '#2563eb' }}>
              Bearbeitungsmodus aktiv
            </span>
          )}
          {!editing && (
            <button
              type="button"
              onClick={() => navigate('/emergency-contacts')}
              data-testid="profile-emergency-contacts"
              style={{
                padding: '0.65rem 1rem', background: '#fff1f2', color: '#b91c1c',
                border: '1px solid #fecaca', borderRadius: '10px', fontWeight: 600,
                cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              🚨 {t('profile.actions.emergencyContacts', 'Notfallkontakte')}
            </button>
          )}
          {!editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="edit-button"
              style={{
                padding: '0.85rem 2.5rem', background: '#2563eb', color: 'white', border: 'none',
                borderRadius: '12px', fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {t('profile.actions.editProfile')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditing(false)}
                data-testid="cancel-button"
                style={{
                  padding: '0.65rem 1.5rem', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1',
                  borderRadius: '10px', fontWeight: 600, cursor: 'pointer'
                }}
              >
                {t('profile.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); document.querySelector('.profile-form')?.requestSubmit(); }}
                data-testid="save-button"
                style={{
                  padding: '0.85rem 2.5rem', background: '#16a34a', color: 'white', border: 'none',
                  borderRadius: '12px', fontWeight: 700, fontSize: '1rem', cursor: 'pointer'
                }}
              >
                {t('profile.actions.saveChanges')}
              </button>
            </>
          )}
        </div>
        
        <form onSubmit={handleUpdate} className="profile-form">
          {/* Personal Data */}
          <div className="form-section">
            <h2>{t('profile.personalInfo')}</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>{t('profile.fields.firstName')}</label>
                <div className="input-wrapper">
                  <User size={18} />
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    disabled={!editing}
                    data-testid="profile-firstName"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>{t('profile.fields.lastName')}</label>
                <div className="input-wrapper">
                  <User size={18} />
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    disabled={!editing}
                    data-testid="profile-lastName"
                  />
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <label>{t('profile.fields.phone')}</label>
              <div className="input-wrapper">
                <Phone size={18} />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  disabled={!editing}
                  data-testid="profile-phone"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>{t('profile.fields.city')}</label>
              <div className="input-wrapper">
                <User size={18} />
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  disabled={!editing}
                  placeholder={t('profile.fields.cityPlaceholder')}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>{t('profile.fields.role')}</label>
              <div className="input-wrapper">
                <Users size={18} />
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  disabled={!editing}
                  data-testid="profile-role"
                >
                  <option value="Fahrer">{t('profile.fields.roles.driver')}</option>
                  <option value="Mitfahrer">{t('profile.fields.roles.passenger')}</option>
                  <option value="Beide">{t('profile.fields.roles.both')}</option>
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label>{t('profile.fields.dateOfBirth')}</label>
              <div className="input-wrapper">
                <Calendar size={18} />
                <input
                  type="text"
                  value={user?.dateOfBirth || ''}
                  disabled
                  data-testid="profile-dob"
                />
              </div>
            </div>

            {/* About me */}
            <div className="form-group">
              <label>{t('profile.fields.bio')}</label>
              <div className="input-wrapper-textarea">
                <MessageSquare size={18} />
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  disabled={!editing}
                  placeholder={t('profile.fields.bioPlaceholder')}
                  rows={4}
                />
              </div>
              <small>{t('profile.fields.bioHint')}</small>
            </div>
          </div>

          {/* Driver Wallet Section */}
          {user?.role !== 'Mitfahrer' && (
            <div className="form-section">
              <DriverWallet />
            </div>
          )}

          {/* Vehicle Section */}
          <div className="form-section">
            <h2><Car size={20} /> {t('profile.vehicle.optional')}</h2>

            {/* Phase A1 — Foto vom Auto */}
            <div data-testid="vehicle-photo-section" style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '0.85rem', marginBottom: '1rem',
              background: '#f8fafc', borderRadius: 10,
              border: '1px solid #e2e8f0'
            }}>
              <div style={{
                width: 88, height: 60, borderRadius: 8,
                background: '#e2e8f0', overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
              }}>
                {vehiclePhotoUrl ? (
                  <img
                    data-testid="vehicle-photo-preview"
                    src={vehiclePhotoUrl.startsWith('http') ? vehiclePhotoUrl : `${API_URL.replace(/\/api$/, '')}${vehiclePhotoUrl}`}
                    alt="Auto"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Car size={28} color="#94a3b8" />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>
                  {t('profile.vehicle.photoTitle', 'Foto vom Auto')}
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {t('profile.vehicle.photoHint', 'Wird Mitfahrern in den Suchergebnissen angezeigt.')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => vehiclePhotoInputRef.current?.click()}
                disabled={vehiclePhotoUploading || !editing}
                data-testid="upload-vehicle-photo-button"
                style={{
                  padding: '0.55rem 0.9rem', borderRadius: 8,
                  background: editing ? '#2563eb' : '#cbd5e1',
                  color: '#fff', border: 'none', cursor: editing ? 'pointer' : 'not-allowed',
                  fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6,
                  flexShrink: 0
                }}
              >
                <Camera size={14} />
                {vehiclePhotoUploading
                  ? t('profile.upload.uploading')
                  : (vehiclePhotoUrl
                      ? t('profile.vehicle.replacePhoto', 'Ändern')
                      : t('profile.vehicle.uploadPhoto', 'Foto hochladen'))}
              </button>
              <input
                ref={vehiclePhotoInputRef}
                type="file"
                accept="image/*"
                onChange={handleVehiclePhotoUpload}
                style={{ display: 'none' }}
                data-testid="vehicle-photo-file-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('profile.vehicle.make')}</label>
                <input
                  type="text"
                  value={formData.vehicle?.make || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    vehicle: {...(formData.vehicle || {}), make: e.target.value}
                  })}
                  disabled={!editing}
                  placeholder={t('profile.vehicle.makePlaceholder')}
                />
              </div>
              
              <div className="form-group">
                <label>{t('profile.vehicle.model')}</label>
                <input
                  type="text"
                  value={formData.vehicle?.model || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    vehicle: {...(formData.vehicle || {}), model: e.target.value}
                  })}
                  disabled={!editing}
                  placeholder={t('profile.vehicle.modelPlaceholder')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('profile.vehicle.color')}</label>
                <input
                  type="text"
                  value={formData.vehicle?.color || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    vehicle: {...(formData.vehicle || {}), color: e.target.value}
                  })}
                  disabled={!editing}
                  placeholder={t('profile.vehicle.colorPlaceholder')}
                />
              </div>
              
              <div className="form-group">
                <label>{t('profile.vehicle.licensePlate')}</label>
                <input
                  type="text"
                  value={formData.vehicle?.license_plate || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    vehicle: {...(formData.vehicle || {}), license_plate: e.target.value}
                  })}
                  disabled={!editing}
                  placeholder="XX-AB 1234"
                  data-testid="vehicle-license-plate-input"
                />
              </div>
            </div>
          </div>

          {/* Preferences Section */}
          <div className="form-section">
            <h2>{t('profile.travelPreferences')}</h2>
            
            <div className="preferences-grid">
              {/* Smoking */}
              <div className="preference-item">
                <label>{t('profile.preferences.smoking')}</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="smoking"
                      value="non-smoker"
                      checked={formData.preferences.smoking === 'non-smoker'}
                      onChange={(e) => setFormData({
                        ...formData,
                        preferences: {...formData.preferences, smoking: e.target.value}
                      })}
                      disabled={!editing}
                    />
                    <span>{t('profile.preferences.smokingOptions.nonSmoker')}</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="smoking"
                      value="allowed"
                      checked={formData.preferences.smoking === 'allowed'}
                      onChange={(e) => setFormData({
                        ...formData,
                        preferences: {...formData.preferences, smoking: e.target.value}
                      })}
                      disabled={!editing}
                    />
                    <span>{t('profile.preferences.smokingOptions.allowed')}</span>
                  </label>
                </div>
              </div>

              {/* Pets */}
              <div className="preference-item">
                <label>{t('profile.preferences.pets')}</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="pets"
                      value="none"
                      checked={formData.preferences.pets === 'none'}
                      onChange={(e) => setFormData({
                        ...formData,
                        preferences: {...formData.preferences, pets: e.target.value}
                      })}
                      disabled={!editing}
                    />
                    <span>{t('profile.preferences.petsOptions.none')}</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="pets"
                      value="welcome"
                      checked={formData.preferences.pets === 'welcome'}
                      onChange={(e) => setFormData({
                        ...formData,
                        preferences: {...formData.preferences, pets: e.target.value}
                      })}
                      disabled={!editing}
                    />
                    <span>{t('profile.preferences.petsOptions.welcome')}</span>
                  </label>
                </div>
              </div>

              {/* Music */}
              <div className="preference-item">
                <label>{t('profile.preferences.music')}</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="music"
                      value="yes"
                      checked={formData.preferences.music === 'yes'}
                      onChange={(e) => setFormData({
                        ...formData,
                        preferences: {...formData.preferences, music: e.target.value}
                      })}
                      disabled={!editing}
                    />
                    <span>{t('profile.preferences.musicOptions.yes')}</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="music"
                      value="quiet"
                      checked={formData.preferences.music === 'quiet'}
                      onChange={(e) => setFormData({
                        ...formData,
                        preferences: {...formData.preferences, music: e.target.value}
                      })}
                      disabled={!editing}
                    />
                    <span>{t('profile.preferences.musicOptions.quiet')}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Security Settings Section */}
          <div className="form-section" data-testid="security-settings-section">
            <h2><Shield size={20} /> {t('security.title')}</h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              {t('security.subtitle')}
            </p>

            {/* Tracking Interval */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <MapPin size={18} style={{ color: '#2563eb' }} />
                <label style={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                  {t('security.trackingInterval.title')}
                </label>
              </div>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                {t('security.trackingInterval.description')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                {[5, 10, 15, 30].map(val => (
                  <button
                    key={val}
                    type="button"
                    data-testid={`tracking-interval-${val}`}
                    disabled={trackingLoading}
                    onClick={() => handleTrackingIntervalChange(val)}
                    style={{
                      padding: '0.65rem 0.75rem',
                      borderRadius: '12px',
                      border: trackingInterval === val ? '2px solid #2563eb' : '2px solid #e2e8f0',
                      background: trackingInterval === val ? '#eff6ff' : '#f8fafc',
                      color: trackingInterval === val ? '#2563eb' : '#475569',
                      fontWeight: trackingInterval === val ? 700 : 500,
                      fontSize: '0.8rem',
                      cursor: trackingLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'left',
                      lineHeight: 1.3,
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                    }}
                  >
                    {t(`security.trackingInterval.options.${val}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Audio Alerts Toggle */}
            <div style={{ marginBottom: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                {audioEnabled ? <Volume2 size={18} style={{ color: '#16a34a' }} /> : <VolumeX size={18} style={{ color: '#94a3b8' }} />}
                <label style={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                  {t('security.audioAlerts.title')}
                </label>
              </div>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                {t('security.audioAlerts.description')}
              </p>
              <button
                type="button"
                onClick={handleAudioToggle}
                data-testid="audio-alerts-toggle"
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.85rem 1.25rem',
                  borderRadius: '12px',
                  border: audioEnabled ? '2px solid #16a34a' : '2px solid #e2e8f0',
                  background: audioEnabled ? '#f0fdf4' : '#f8fafc',
                  color: audioEnabled ? '#16a34a' : '#64748b',
                  fontWeight: 600, fontSize: '0.9rem',
                  cursor: 'pointer', transition: 'all 0.2s', width: '100%',
                }}
              >
                <div style={{
                  width: '44px', height: '24px', borderRadius: '12px',
                  background: audioEnabled ? '#16a34a' : '#cbd5e1',
                  position: 'relative', transition: 'background 0.2s',
                  flexShrink: 0,
                }}>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: '#fff', position: 'absolute', top: '2px',
                    left: audioEnabled ? '22px' : '2px',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </div>
                {t('security.audioAlerts.enable')}
              </button>
            </div>

            {/* Push Notifications Toggle */}
            {pushSupported && (
              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  {pushSubscribed ? <Bell size={18} style={{ color: '#2563eb' }} /> : <BellOff size={18} style={{ color: '#94a3b8' }} />}
                  <label style={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                    {t('security.pushNotifications.title')}
                  </label>
                  {/* SW ready indicator */}
                  <span data-testid="sw-status-indicator" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    fontSize: '0.7rem', fontWeight: 600, marginLeft: 'auto',
                    color: swReady ? '#16a34a' : '#94a3b8',
                  }}>
                    <span style={{
                      width: '6px', height: '6px', borderRadius: '50%',
                      background: swReady ? '#16a34a' : '#94a3b8',
                    }} />
                    {swReady ? 'SW Ready' : 'SW Loading...'}
                  </span>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                  {t('security.pushNotifications.description')}
                </p>

                {/* Permission denied banner */}
                {pushPermission === 'denied' && !pushSubscribed && (
                  <div data-testid="push-permission-denied" style={{
                    padding: '0.75rem 1rem', marginBottom: '0.75rem', borderRadius: '10px',
                    background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
                    fontSize: '0.85rem', lineHeight: 1.5,
                  }}>
                    {t('security.pushNotifications.deniedBanner')}
                  </div>
                )}

                {(() => {
                  const isDenied = pushPermission === 'denied' && !pushSubscribed;
                  const isClickable = !pushLoading && !isDenied;
                  return (
                    <button
                      type="button"
                      onClick={handlePushToggle}
                      disabled={!isClickable}
                      data-testid="push-notifications-toggle"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.85rem 1.25rem',
                        borderRadius: '12px',
                        border: pushSubscribed ? '2px solid #2563eb' : isDenied ? '2px solid #e2e8f0' : '2px solid #94a3b8',
                        background: pushSubscribed ? '#eff6ff' : isDenied ? '#f8fafc' : '#fff',
                        color: pushSubscribed ? '#2563eb' : isDenied ? '#cbd5e1' : '#334155',
                        fontWeight: 600, fontSize: '0.9rem',
                        cursor: isClickable ? 'pointer' : 'not-allowed',
                        opacity: pushLoading ? 0.6 : isDenied ? 0.5 : 1,
                        transition: 'all 0.2s', width: '100%',
                      }}
                    >
                      <div style={{
                        width: '44px', height: '24px', borderRadius: '12px',
                        background: pushSubscribed ? '#2563eb' : isDenied ? '#e2e8f0' : '#94a3b8',
                        position: 'relative', transition: 'background 0.2s',
                        flexShrink: 0,
                      }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '50%',
                          background: '#fff', position: 'absolute', top: '2px',
                          left: pushSubscribed ? '22px' : '2px',
                          transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </div>
                      {pushLoading ? t('common.loading') : t('security.pushNotifications.enable')}
                    </button>
                  );
                })()}
                {pushSubscribed && (
                  <button
                    type="button"
                    onClick={handleTestPush}
                    data-testid="push-test-button"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.65rem 1.25rem', marginTop: '0.75rem',
                      borderRadius: '10px',
                      border: '1.5px solid #2563eb',
                      background: '#fff', color: '#2563eb',
                      fontWeight: 600, fontSize: '0.85rem',
                      cursor: 'pointer', transition: 'all 0.2s',
                    }}
                  >
                    <Bell size={16} />
                    {t('security.pushNotifications.testButton')}
                  </button>
                )}
              </div>
            )}
          </div>
          
        </form>
        
        <button
          onClick={handleLogout}
          className="logout-button"
          data-testid="logout-button"
        >
          <LogOut size={18} />
          {t('common.logout')}
        </button>

        {/* Danger Zone - Delete Account */}
        <div className="danger-zone">
          <div className="danger-header">
            <AlertTriangle size={20} />
            <h3>{t('profile.dangerZone.title')}</h3>
          </div>
          <p>{t('profile.dangerZone.description')}</p>
          <button
            onClick={() => {
              if (window.confirm(t('profile.dangerZone.confirmDelete'))) {
                handleDeleteAccount();
              }
            }}
            className="delete-account-button"
            data-testid="delete-account-button"
          >
            {t('profile.dangerZone.deleteAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};